import { ArrowBack, ArrowRight, Close } from "@mui/icons-material";
import { AppBar, Box, IconButton, List, ListItem, ListItemText, Toolbar, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { trackEvent } from "../../../utils/analytics";
import { toKebabCase } from "../../../utils/strings";

// Define the notification types enum to match the database
export enum NotificationSettingsType {
	NewOpenShift = "NewOpenShift",
	ShiftChanges = "ShiftChanges",
	ShiftStartEnd = "ShiftStartEnd",
	PersonalRequests = "PersonalRequests",
}

export const NotificationSettings: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();
	const { userInfo } = useAuth();
	const hasPushToken = !!userInfo?.push_notification_token;

	const NOTIFICATION_TYPE_CONFIG: Readonly<Record<NotificationSettingsType, { label: string; description: string }>> = {
		[NotificationSettingsType.NewOpenShift]: {
			label: t("notif.new.open.shift"),
			description: t("notif.new.open.shift.desc"),
		},
		[NotificationSettingsType.ShiftChanges]: {
			label: t("notif.shift.changes"),
			description: t("notif.shift.changes.desc"),
		},
		[NotificationSettingsType.ShiftStartEnd]: {
			label: t("notif.shift.start.end"),
			description: t("notif.shift.start.end.desc"),
		},
		[NotificationSettingsType.PersonalRequests]: {
			label: t("notif.personal.requests"),
			description: t("notif.personal.requests.desc"),
		},
	};

	const handleBack = () => {
		trackEvent("notification_settings_back_clicked", { userId: userInfo?.id });
		const path = `/${chosenDepartment.department_id}/settings`;
		navigate(path);
	};

	const handleClose = () => {
		trackEvent("notification_settings_close_clicked", { userId: userInfo?.id });
		const path = `/${chosenDepartment.department_id}/settings`;
		navigate(path);
	};

	const handleNotificationTypeClick = (notificationType: NotificationSettingsType) => {
		trackEvent("notification_type_clicked", {
			userId: userInfo?.id,
			notificationType: notificationType,
			hasPushToken: hasPushToken,
		});
		const path = `/${chosenDepartment.department_id}/settings/notification-settings/${toKebabCase(notificationType)}`;
		navigate(path);
	};

	return (
		<Box
			sx={{
				height: "100vh",
				backgroundColor: "#fff",
				position: "relative",
				overflow: "hidden",
			}}
		>
			{/* AppBar Header */}
			<AppBar
				position="static"
				elevation={0}
				sx={{
					backgroundColor: "#fff",
					color: "#000",
					marginTop: "60px",
				}}
			>
				<Toolbar>
					{/* Left side: Back Arrow */}
					<IconButton edge="start" onClick={handleBack} sx={{ p: 0.5 }}>
						<ArrowBack />
					</IconButton>

					{/* Center: Title */}
					<Typography
						variant="h6"
						sx={{
							flexGrow: 1,
							textAlign: "center",
							fontWeight: 600,
							color: "inherit",
						}}
					>
						{t("notification.settings")}
					</Typography>

					{/* Right side: Close Button (X) */}
					<IconButton
						edge="end"
						onClick={handleClose}
						sx={{
							p: 0.5,
							"& .MuiSvgIcon-root": {
								fontSize: "18px",
							},
						}}
					>
						<Close />
					</IconButton>
				</Toolbar>
			</AppBar>
			{/* Main notification settings */}
			<Box
				sx={{
					mx: 2,
					backgroundColor: "#fff",
					borderRadius: 2,
					border: "1px solid #e9ecef",
					boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
				}}
			>
				{/* Push notification status message */}
				{!hasPushToken && (
					<Box
						sx={{
							px: 3,
							py: 2,
							backgroundColor: "#f8f9fa",
							borderBottom: "1px solid #f0f0f0",
						}}
					>
						<Typography
							variant="body2"
							sx={{
								color: "#666",
								fontSize: "14px",
								lineHeight: 1.4,
							}}
						>
							{t("download.app.push.notifications")}
						</Typography>
					</Box>
				)}

				{/* Notification Types List */}
				<List sx={{ p: 0 }}>
					{Object.values(NotificationSettingsType).map((notificationType, index) => {
						const config = NOTIFICATION_TYPE_CONFIG[notificationType];
						return (
							<ListItem
								key={notificationType}
								onClick={() => handleNotificationTypeClick(notificationType)}
								sx={{
									py: 2.5,
									px: 3,
									borderBottom: index < Object.values(NotificationSettingsType).length - 1 ? "1px solid #f0f0f0" : "none",
									cursor: "pointer",
									"&:hover": {
										backgroundColor: "#f5f5f5",
									},
								}}
							>
								<ListItemText
									primary={config.label}
									secondary={config.description}
									slotProps={{
										primary: {
											sx: {
												fontSize: "16px",
												fontWeight: 500,
											},
										},

										secondary: {
											sx: {
												fontSize: "14px",
												color: "#666",
											},
										},
									}}
								/>
								<ArrowRight sx={{ color: "#999" }} />
							</ListItem>
						);
					})}
				</List>
			</Box>
		</Box>
	);
};
